import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip as MuiChip,
  IconButton as MuiIconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Groups,
  LocationOn,
  Schedule,
} from '@mui/icons-material';

export const surfaceShadow = '0 4px 20px rgba(29, 29, 27, 0.035)';

export function PageContainer({ children, maxWidth = '1200px', withBottomNav = true, sx = {} }) {
  return (
    <Box
      component="main"
      sx={{
        width: '100%',
        maxWidth,
        mx: 'auto',
        px: { xs: 2, sm: 3 },
        pt: 3,
        pb: withBottomNav ? 'calc(var(--cv-bottom-nav-height) + 30px)' : 4,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function SurfaceCard({ children, sx = {}, variant = 'default', ...props }) {
  return (
    <Paper
      elevation={0}
      {...props}
      sx={{
        bgcolor: variant === 'tonal' ? 'background.surfaceLow' : 'background.paper',
        borderRadius: 2,
        p: { xs: 2, sm: 3 },
        boxShadow: surfaceShadow,
        border: '1px solid',
        borderColor: 'rgba(80, 60, 60, 0.08)',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

export function ActionButton({ children, variant = 'primary', icon, sx = {}, ...props }) {
  const variants = {
    primary: {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      '&:hover': { bgcolor: 'primary.dark' },
    },
    container: {
      bgcolor: 'primary.container',
      color: '#fff',
      '&:hover': { bgcolor: 'primary.main' },
    },
    secondary: {
      color: 'secondary.main',
      borderColor: 'secondary.main',
      bgcolor: 'transparent',
      '&:hover': { bgcolor: 'rgba(37, 93, 173, 0.06)' },
    },
    quiet: {
      color: 'primary.main',
      bgcolor: 'primary.fixed',
      '&:hover': { bgcolor: '#ffb2bb' },
    },
  };

  return (
    <Button
      fullWidth
      variant={variant === 'secondary' ? 'outlined' : 'contained'}
      {...props}
      sx={{
        minHeight: 44,
        borderRadius: 2.5,
        fontWeight: 600,
        px: 2,
        py: 1,
        gap: 1,
        boxShadow: variant === 'primary' || variant === 'container' ? '0 2px 6px rgba(97, 0, 31, 0.10)' : 'none',
        ...variants[variant],
        ...sx,
      }}
    >
      {icon}
      {children}
    </Button>
  );
}

export function IconButton({ children, label, sx = {}, ...props }) {
  return (
    <MuiIconButton
      aria-label={label}
      {...props}
      sx={{
        width: 44,
        height: 44,
        color: 'primary.main',
        bgcolor: 'transparent',
        '&:hover': { bgcolor: 'rgba(229, 226, 222, 0.55)' },
        ...sx,
      }}
    >
      {children}
    </MuiIconButton>
  );
}

export function Chip({ children, selected = false, sx = {}, ...props }) {
  return (
    <MuiChip
      label={children}
      {...props}
      sx={{
        borderRadius: 999,
        fontFamily: 'inherit',
        fontWeight: 600,
        bgcolor: selected ? 'secondary.container' : 'background.surfaceContainer',
        color: selected ? 'secondary.dark' : 'text.secondary',
        border: '1px solid',
        borderColor: selected ? 'secondary.container' : 'outline.variant',
        ...sx,
      }}
    />
  );
}

export function SectionHeader({ icon, title, eyebrow, action, sx = {} }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, ...sx }}>
      <Stack direction="row" alignItems="center" spacing={1.25}>
        {icon && <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>}
        <Box>
          {eyebrow && (
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              {eyebrow}
            </Typography>
          )}
          <Typography variant="h3" sx={{ color: 'primary.main' }}>
            {title}
          </Typography>
        </Box>
      </Stack>
      {action}
    </Stack>
  );
}

export function HeaderTriggerTabs({ value, options = [], onChange, sx = {} }) {
  return (
    <Stack
      direction="row"
      spacing={0.25}
      sx={{
        bgcolor: 'rgba(255,255,255,0.72)',
        border: '1px solid rgba(223,191,193,0.5)',
        borderRadius: 999,
        p: 0.25,
        minWidth: 0,
        ...sx,
      }}
    >
      {options.map((option) => {
        const active = value === option.id;
        return (
          <Button
            key={option.id}
            onClick={() => onChange(option.id)}
            startIcon={option.icon}
            aria-label={option.label}
            aria-pressed={active}
            sx={{
              minWidth: 0,
              borderRadius: 999,
              px: { xs: 1, sm: 1.5 },
              py: 0.45,
              fontSize: { xs: 12, sm: 13 },
              color: active ? '#fff' : 'primary.main',
              bgcolor: active ? 'primary.main' : 'transparent',
              '& .MuiButton-startIcon': {
                mr: { xs: 0, sm: 0.5 },
              },
              '&:hover': {
                bgcolor: active ? 'primary.dark' : 'primary.fixed',
              },
            }}
          >
            <Box component="span">
              {option.label}
            </Box>
          </Button>
        );
      })}
    </Stack>
  );
}

export function ParticipantCard({ name, role, avatar, isHost = false, onClick, sx = {} }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: isHost ? 'rgba(138, 0, 48, 0.22)' : 'rgba(223, 191, 193, 0.45)',
        bgcolor: isHost ? 'rgba(255, 217, 221, 0.18)' : 'background.paper',
        minHeight: 84,
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Avatar src={avatar} alt={name} sx={{ width: 56, height: 56, border: isHost ? '2px solid #8a0030' : '1px solid #e5e2de' }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" sx={{ fontSize: 18, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name || 'Partecipante'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {isHost ? 'Organizzatore' : role || 'Partecipante'}
        </Typography>
      </Box>
    </Box>
  );
}

export function TableHero({ title, image, statusLabel, eyebrow, date, location, seats, compact = false }) {
  return (
    <Box
      sx={{
        position: 'relative',
        height: compact ? 280 : { xs: 340, md: 420 },
        borderRadius: { xs: 3, md: 4 },
        overflow: 'hidden',
        boxShadow: surfaceShadow,
      }}
    >
      <Box
        component="img"
        src={image || `${process.env.REACT_APP_BASE_PATH || ''}/assets/logo.png`}
        alt={title}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', bgcolor: 'background.surfaceHigh' }}
      />
      <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.78))' }} />
      {statusLabel && (
        <Box sx={{ position: 'absolute', top: 18, left: 18, bgcolor: statusLabel === 'LIVE NOW' ? '#ffdad6' : 'primary.fixed', color: 'primary.main', px: 2, py: 0.75, borderRadius: 999, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusLabel === 'LIVE NOW' ? 'error.main' : 'primary.main' }} />
          {statusLabel}
        </Box>
      )}
      <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: { xs: 3, md: 5 }, color: '#fff' }}>
        {eyebrow && (
          <Typography variant="caption" sx={{ letterSpacing: '0.16em', textTransform: 'uppercase', color: '#ffd9dd' }}>
            {eyebrow}
          </Typography>
        )}
        <Typography variant="h1" sx={{ fontSize: { xs: 30, md: 42 }, lineHeight: 1.2, color: '#fff', maxWidth: 720 }}>
          {title}
        </Typography>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
          {date && <HeroPill icon={<Schedule fontSize="small" />} text={date} />}
          {location && <HeroPill icon={<LocationOn fontSize="small" />} text={location} />}
          {seats && <HeroPill icon={<Groups fontSize="small" />} text={seats} />}
        </Stack>
      </Box>
    </Box>
  );
}

function HeroPill({ icon, text }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ bgcolor: 'rgba(0,0,0,0.34)', px: 1.5, py: 0.75, borderRadius: 999, backdropFilter: 'blur(10px)' }}>
      {icon}
      <Typography variant="button" sx={{ color: '#fff' }}>{text}</Typography>
    </Stack>
  );
}

export function TableMetaGrid({ items = [] }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
      {items.map((item) => (
        <Box key={item.label}>
          <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {item.label}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
            <Box sx={{ color: item.color || 'primary.main', display: 'flex' }}>{item.icon}</Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: item.color || 'text.primary' }}>
              {item.value}
            </Typography>
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

export function LocationCard({ title, address, lat, lng, onDirections }) {
  const latitude = Number(lat);
  const longitude = Number(lng);
  const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
  const mapQuery = hasCoordinates
    ? `${latitude},${longitude}`
    : (address || title || '');
  const mapSrc = mapQuery
    ? `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=16&output=embed`
    : '';

  return (
    <SurfaceCard sx={{ p: 0 }}>
      <Box sx={{ p: 2.5, bgcolor: 'background.surfaceLow', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h4">{title || 'Location da confermare'}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{address || 'Indirizzo non disponibile'}</Typography>
        </Box>
        <IconButton label="Indicazioni" onClick={onDirections} sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}>
          <LocationOn />
        </IconButton>
      </Box>
      <Box sx={{ height: 190, position: 'relative', bgcolor: 'background.surfaceHigh', overflow: 'hidden' }}>
        {mapSrc ? (
          <Box
            component="iframe"
            title={`Mappa ${title || address || 'location'}`}
            src={mapSrc}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
          />
        ) : (
          <>
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #dcdad6 0%, #f0edea 45%, #c7c7c7 100%)' }} />
            <LocationOn sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 58, color: 'primary.main' }} />
          </>
        )}
      </Box>
    </SurfaceCard>
  );
}

export function RulesList({ rules = [] }) {
  const finalRules = rules.length ? rules : [
    { title: 'Puntualita', text: "Gradita la presenza 10 minuti prima dell'inizio." },
    { title: 'Digital Detox', text: 'Telefoni silenziati per favorire la conversazione.' },
    { title: 'Privacy', text: 'Rispetto della riservatezza professionale condivisa.' },
  ];

  return (
    <Stack spacing={1.5}>
      {finalRules.map((rule) => (
        <Stack key={rule.title} direction="row" spacing={1.25} alignItems="flex-start">
          <CheckCircle sx={{ color: 'secondary.main', fontSize: 22, mt: 0.1 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{rule.title}: </Box>
            {rule.text}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export function NotificationCard({ title, body, time, type = 'tables', onClick }) {
  return (
    <SurfaceCard
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      } : undefined}
      sx={{
        p: 2.5,
        borderLeft: '4px solid',
        borderLeftColor: type === 'requests' ? 'secondary.main' : 'primary.main',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="h4" sx={{ fontSize: 16 }}>{title}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{time}</Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>{body}</Typography>
    </SurfaceCard>
  );
}
